// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { ActivityIndicator, Image, Modal, Platform, Pressable, ScrollView, Share, StyleSheet, TextInput, View, type ImageSourcePropType } from 'react-native';
import { Redirect, useLocalSearchParams, useRootNavigationState, useRouter } from 'expo-router';
import { useIsFocused } from '@react-navigation/native';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import QRCode from 'react-native-qrcode-svg';

import { Text } from '@/components/Themed';
import GoffyButton from '@/components/GoffyButton';
import RoundGameInfoModal from '@/components/RoundGameInfoModal';
import { useI18n } from '@/i18n';
import { useUser } from '@/app/user-context';
import {
  getSharedBffWebSocket,
  RoundGroupsUpdateRejectedEvent,
  RoundGroupsUpdatedEvent,
  RoundUpdatedEvent,
} from '@/services/bffWebSocket';
import { getApiBaseUrl } from '@/services/apiBaseUrl';
import { buildRoundUpdateMessage } from '@/services/roundUpdateMessage';
import { createAppLogger } from '@/services/appLogger';
import { setCurrentRoundId } from '@/services/currentRound';
import { isMysteryAcknowledged } from '@/services/mysterySetup';
import { getAssetImageSource } from '@/services/assetImageSource';
import { applyPlayerSwap, buildRoundGroupsLayout, type RoundGroupsLayout } from '@/services/roundGroupsLayout';
import { resolveTourInviteLink } from '@/services/tourInviteLink';
import appColors from '@/theme/colors';
import { parseHandicapValue } from '@/features/hole/utils';

type RoundResponse = {
  id?: string;
  Id?: string;
  name?: string;
  Name?: string;
  status?: string;
  Status?: string;
  tourId?: string;
  TourId?: string;
  gameDefinitionIds?: string[];
  GameDefinitionIds?: string[];
  gameWeights?: Array<{ gameDefinitionId?: string; weightPercent?: number }>;
  GameWeights?: Array<{ gameDefinitionId?: string; weightPercent?: number }>;
  players?: Array<Record<string, unknown>>;
  Players?: Array<Record<string, unknown>>;
};

type RoundGroupsResponse = {
  playerOrder?: string[];
  PlayerOrder?: string[];
  canEdit?: boolean;
  CanEdit?: boolean;
};

type RoundPlayer = {
  id: string;
  name: string;
  userId?: string | null;
  isTemporary?: boolean;
  playingHandicap?: number | null;
};

type GameDefinition = {
  id: string;
  name: string;
  info?: string | null;
  description?: string | null;
  competitionType?: string | null;
  image?: string | null;
  video?: string | null;
  videoWebWidth?: number | null;
  videoWebHeight?: number | null;
  videoMobileWidth?: number | null;
  videoMobileHeight?: number | null;
  status?: string | null;
  defaultWeightPercent?: number | null;
};

const MEDIA_PAGE_PATH = '/round/[roundId]/roundstart';
const FALLBACK_GAME_IMAGE = getAssetImageSource('images/games/mystery-select.png', 'game-select', MEDIA_PAGE_PATH);

const GROUP_UPDATE_TIMEOUT_MS = 8000;
type CardBounds = { left: number; top: number; right: number; bottom: number };
type MeasurableCard = { measureInWindow?: (callback: (x: number, y: number, width: number, height: number) => void) => void };

const generateRequestId = () => {
  const uuid = (globalThis.crypto as { randomUUID?: () => string } | undefined)?.randomUUID?.();
  if (uuid) return uuid;
  // Fallback UUID v4-like shape for environments without crypto.randomUUID.
  const segment = () => Math.floor((1 + Math.random()) * 0x10000).toString(16).slice(1);
  return `${segment()}${segment()}-${segment()}-4${segment().slice(1)}-a${segment().slice(1)}-${segment()}${segment()}${segment()}`;
};

export default function RoundStartScreen() {
  const apiBaseUrl = useMemo(() => getApiBaseUrl(), []);
  const { t } = useI18n();
  const { user } = useUser();
  const router = useRouter();
  const navState = useRootNavigationState();
  const isNavigationReady = !!navState?.key;
  const isFocused = useIsFocused();
  const { roundId: roundIdParam, debug: debugParam, mode: modeParam } = useLocalSearchParams<{ roundId?: string; debug?: string; mode?: string }>();
  const debug = useMemo(() => debugParam === 'true', [debugParam]);
  const updateMode = useMemo(() => {
    const raw = Array.isArray(modeParam) ? modeParam[0] : modeParam;
    return (raw ?? '').toLowerCase() === 'update';
  }, [modeParam]);
  const roundId = useMemo(() => {
    const raw = Array.isArray(roundIdParam) ? roundIdParam[0] : roundIdParam;
    return raw ?? null;
  }, [roundIdParam]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusNote, setStatusNote] = useState('');
  const [roundName, setRoundName] = useState<string | null>(null);
  const [tourId, setTourId] = useState<string | null>(null);
  const [players, setPlayers] = useState<RoundPlayer[]>([]);
  const [roundGames, setRoundGames] = useState<GameDefinition[]>([]);
  const [selectedGameIds, setSelectedGameIds] = useState<Set<string>>(new Set());
  // When a round includes the Mystery game but its subgames haven't been assigned yet, send the
  // user to the mystery-prize selection screen on open/start (once). Skipped in round-settings
  // (update) mode so editing settings doesn't bounce away.
  const mysteryRedirectCheckedRef = useRef(false);
  useEffect(() => {
    if (mysteryRedirectCheckedRef.current) return;
    if (loading || updateMode || !roundId || !user?.token) return;
    const hasMystery = roundGames.some(
      (game) => selectedGameIds.has(game.id) && (game.name ?? '').toLowerCase() === 'mystery'
    );
    mysteryRedirectCheckedRef.current = true;
    if (!hasMystery) return;
    let cancelled = false;
    (async () => {
      try {
        // Mystery setup is optional; skip the redirect once the user has saved or skipped it for
        // this round (the assignment write is async, so subgames may still read as empty).
        if (await isMysteryAcknowledged(roundId)) return;
        const response = await fetch(`${apiBaseUrl}/api/rounds/${roundId}/subgames`, {
          headers: { Authorization: `Bearer ${user.token}` },
        });
        if (!response.ok) return;
        const data = (await response.json()) as unknown;
        const count = Array.isArray(data) ? data.length : 0;
        if (!cancelled && count === 0) {
          router.replace(`/round/${roundId}/mystery-prize?tourId=${tourId ?? ''}&debug=${debug ? 'true' : 'false'}`);
        }
      } catch {
        // Ignore; the mystery prize can still be set from the menu.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [apiBaseUrl, loading, roundGames, selectedGameIds, updateMode, roundId, user?.token, router, tourId, debug]);
  const [weightsByGameId, setWeightsByGameId] = useState<Record<string, number>>({});
  const [selectedPlayersById, setSelectedPlayersById] = useState<Record<string, boolean>>({});
  const [playingHandicapByPlayerId, setPlayingHandicapByPlayerId] = useState<Record<string, string>>({});
  const [savedPlayingHandicapByPlayerId, setSavedPlayingHandicapByPlayerId] = useState<Record<string, string>>({});
  const [infoOpen, setInfoOpen] = useState(false);
  const [infoGame, setInfoGame] = useState<GameDefinition | null>(null);
  const [openingRound, setOpeningRound] = useState(false);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [shareModalLink, setShareModalLink] = useState<string | null>(null);
  const [shareModalLoading, setShareModalLoading] = useState(false);
  const [shareModalError, setShareModalError] = useState<string | null>(null);
  const [groupLayout, setGroupLayout] = useState<RoundGroupsLayout | null>(null);
  const [groupCanEdit, setGroupCanEdit] = useState(true);
  const [selectedSwapPlayerId, setSelectedSwapPlayerId] = useState<string | null>(null);
  const [groupSavePending, setGroupSavePending] = useState(false);
  const [groupSaveError, setGroupSaveError] = useState<string | null>(null);
  const [draggingPlayerId, setDraggingPlayerId] = useState<string | null>(null);
  const [dragHoverPlayerId, setDragHoverPlayerId] = useState<string | null>(null);
  const pendingGroupUpdateRef = useRef<{
    requestId: string;
    previousOrder: string[];
  } | null>(null);
  const groupUpdateTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const playerCardRefs = useRef<Record<string, MeasurableCard | null>>({});
  const playerCardBoundsRef = useRef<Record<string, CardBounds>>({});

  const logToBff = useMemo(
    () =>
      createAppLogger(apiBaseUrl, user?.token, {
        source: 'round-start',
      }),
    [apiBaseUrl, user?.token]
  );
  const playerNameById = useMemo(() => {
    const next: Record<string, string> = {};
    players.forEach((player) => {
      next[player.id] = player.name || player.id;
    });
    return next;
  }, [players]);

  const loadSnapshot = useCallback(async () => {
    if (!roundId || !user?.token) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const [roundResponse, playersResponse, gameDefinitionsResponse, groupsResponse] = await Promise.all([
        fetch(`${apiBaseUrl}/api/rounds/${roundId}`, {
          headers: { Authorization: `Bearer ${user.token}` },
        }),
        fetch(`${apiBaseUrl}/api/rounds/${roundId}/players`, {
          headers: { Authorization: `Bearer ${user.token}` },
        }),
        fetch(`${apiBaseUrl}/api/game-definitions`, {
          headers: { Authorization: `Bearer ${user.token}` },
        }),
        fetch(`${apiBaseUrl}/api/rounds/${roundId}/groups`, {
          headers: { Authorization: `Bearer ${user.token}` },
        }),
      ]);

      if (!roundResponse.ok || !playersResponse.ok || !gameDefinitionsResponse.ok) {
        throw new Error(t('roundStartSetup.loadError'));
      }

      const roundData = (await roundResponse.json()) as RoundResponse;
      const playersData = await playersResponse.json();
      const gameDefinitionsRaw = (await gameDefinitionsResponse.json()) as Array<Record<string, unknown>>;
      const groupsData = groupsResponse.ok
        ? ((await groupsResponse.json()) as RoundGroupsResponse)
        : null;

      const normalizedRoundName = (roundData.name ?? roundData.Name ?? '').trim();
      setRoundName(normalizedRoundName || null);
      setTourId((roundData.tourId ?? roundData.TourId ?? null) as string | null);

      const roundGameIds = Array.from(
        new Set((roundData.gameDefinitionIds ?? roundData.GameDefinitionIds ?? []).filter((id): id is string => typeof id === 'string' && id.length > 0))
      );

      const playersPayload = Array.isArray(playersData) ? playersData : (playersData.players ?? playersData.Players ?? []);
      const roundPlayersPayload = Array.isArray(roundData.players)
        ? roundData.players
        : (Array.isArray(roundData.Players) ? roundData.Players : []);
      const roundHandicapByPlayerId = new Map<string, number | null>();
      roundPlayersPayload.forEach((entry: Record<string, unknown>) => {
        const playerId = String(entry.id ?? entry.Id ?? entry.playerId ?? entry.PlayerId ?? '');
        if (!playerId) return;
        const handicap = parseHandicapValue(
          entry.playingHandicap ??
            entry.PlayingHandicap ??
            entry.playing_handicap ??
            entry.Playing_Handicap ??
            entry.shcp ??
            entry.Shcp ??
            entry.scp ??
            entry.Scp
        );
        roundHandicapByPlayerId.set(playerId, handicap ?? null);
      });

      const normalizedPlayers = playersPayload
        .map((entry: Record<string, unknown>) => {
          const playerId = String(entry.id ?? entry.Id ?? '');
          const roundScopedHandicap = roundHandicapByPlayerId.get(playerId);
          const playerScopedHandicap = parseHandicapValue(
            entry.playingHandicap ??
              entry.PlayingHandicap ??
              entry.playing_handicap ??
              entry.Playing_Handicap ??
              entry.shcp ??
              entry.Shcp ??
              entry.scp ??
              entry.Scp
          );
          return {
            id: playerId,
            name: String(entry.name ?? entry.Name ?? ''),
            userId: (entry.userId ?? entry.UserId ?? null) as string | null,
            isTemporary: Boolean(entry.isTemporary ?? entry.IsTemporary ?? false),
            // Always prefer round-specific SHCP when available for this player.
            playingHandicap: roundScopedHandicap !== undefined ? roundScopedHandicap : (playerScopedHandicap ?? null),
          };
        })
        .filter((entry: RoundPlayer) => !!entry.id);
      setPlayers(normalizedPlayers);
      setSelectedPlayersById((prev) => {
        const next: Record<string, boolean> = {};
        normalizedPlayers.forEach((player) => {
          next[player.id] = prev[player.id] ?? true;
        });
        return next;
      });
      const baselinePlayerOrder = normalizedPlayers.map((player) => player.id);
      const incomingManualOrder = groupsData
        ? (groupsData.playerOrder ?? groupsData.PlayerOrder ?? []).filter((playerId): playerId is string => typeof playerId === 'string')
        : null;
      setGroupCanEdit(
        groupsData
          ? Boolean(groupsData.canEdit ?? groupsData.CanEdit ?? ((roundData.status ?? roundData.Status ?? '').toLowerCase() === 'draft'))
          : ((roundData.status ?? roundData.Status ?? '').toLowerCase() === 'draft')
      );
      setGroupLayout(buildRoundGroupsLayout(roundId, baselinePlayerOrder, incomingManualOrder));
      setSelectedSwapPlayerId(null);
      setGroupSavePending(false);
      setGroupSaveError(null);
      pendingGroupUpdateRef.current = null;
      setPlayingHandicapByPlayerId((prev) => {
        const next: Record<string, string> = {};
        normalizedPlayers.forEach((player) => {
          if (typeof player.playingHandicap === 'number' && Number.isFinite(player.playingHandicap)) {
            next[player.id] = String(player.playingHandicap);
            return;
          }
          const existing = prev[player.id];
          next[player.id] = typeof existing === 'string' ? existing : '';
        });
        return next;
      });
      setSavedPlayingHandicapByPlayerId(() => {
        const next: Record<string, string> = {};
        normalizedPlayers.forEach((player) => {
          if (typeof player.playingHandicap === 'number' && Number.isFinite(player.playingHandicap)) {
            next[player.id] = String(player.playingHandicap);
          } else {
            next[player.id] = '';
          }
        });
        return next;
      });

      const definitions = (gameDefinitionsRaw ?? [])
        .map((entry) => ({
          id: String(entry['id'] ?? entry['Id'] ?? ''),
          name: String(entry['name'] ?? entry['Name'] ?? ''),
          info: (entry['info'] as string | null | undefined) ?? (entry['Info'] as string | null | undefined) ?? null,
          description:
            (entry['description'] as string | null | undefined) ??
            (entry['Description'] as string | null | undefined) ??
            null,
          competitionType:
            (entry['competitionType'] as string | null | undefined) ??
            (entry['CompetitionType'] as string | null | undefined) ??
            null,
          image: (entry['image'] as string | null | undefined) ?? (entry['Image'] as string | null | undefined) ?? null,
          video: (entry['video'] as string | null | undefined) ?? (entry['Video'] as string | null | undefined) ?? null,
          videoWebWidth:
            (entry['videoWebWidth'] as number | null | undefined) ??
            (entry['VideoWebWidth'] as number | null | undefined) ??
            null,
          videoWebHeight:
            (entry['videoWebHeight'] as number | null | undefined) ??
            (entry['VideoWebHeight'] as number | null | undefined) ??
            null,
          videoMobileWidth:
            (entry['videoMobileWidth'] as number | null | undefined) ??
            (entry['VideoMobileWidth'] as number | null | undefined) ??
            null,
          videoMobileHeight:
            (entry['videoMobileHeight'] as number | null | undefined) ??
            (entry['VideoMobileHeight'] as number | null | undefined) ??
            null,
          status: (entry['status'] as string | null | undefined) ?? (entry['Status'] as string | null | undefined) ?? null,
          defaultWeightPercent:
            (entry['defaultWeightPercent'] as number | null | undefined) ??
            (entry['DefaultWeightPercent'] as number | null | undefined) ??
            100,
        }))
        .filter((entry) => !!entry.id);

      const definitionsById = new Map(definitions.map((entry) => [entry.id, entry]));
      const mappedRoundGames = roundGameIds.map((gameId) => {
        const definition = definitionsById.get(gameId);
        return definition ?? {
          id: gameId,
          name: gameId,
          status: null,
          defaultWeightPercent: 100,
          info: null,
          description: null,
          competitionType: null,
          image: null,
          video: null,
        };
      });
      setRoundGames(mappedRoundGames);
      setSelectedGameIds(new Set(roundGameIds));

      const rawWeights = roundData.gameWeights ?? roundData.GameWeights ?? [];
      const mappedWeights: Record<string, number> = {};
      rawWeights.forEach((entry) => {
        if (entry?.gameDefinitionId && Number.isFinite(entry.weightPercent)) {
          mappedWeights[entry.gameDefinitionId] = Number(entry.weightPercent);
        }
      });
      mappedRoundGames.forEach((game) => {
        if (!Number.isFinite(mappedWeights[game.id])) {
          mappedWeights[game.id] = Number(game.defaultWeightPercent ?? 100);
        }
      });
      setWeightsByGameId(mappedWeights);
      setStatusNote('');
    } catch (loadErr) {
      setError(loadErr instanceof Error ? loadErr.message : t('roundStartSetup.loadError'));
    } finally {
      setLoading(false);
    }
  }, [apiBaseUrl, roundId, t, user?.token]);

  const sendRoundUpdate = useCallback(
    (nextSelectedGameIds: Set<string>, overrideWeightsByGameId?: Record<string, number>) => {
      if (!roundId || !user?.token) return;
      const orderedGameIds = roundGames.map((game) => game.id).filter((id) => nextSelectedGameIds.has(id));
      const weights = overrideWeightsByGameId ?? weightsByGameId;
      const payload = buildRoundUpdateMessage({
        roundId,
        gameDefinitionIds: orderedGameIds,
        gameWeights: orderedGameIds.map((id) => ({
          gameDefinitionId: id,
          weightPercent: Number.isFinite(weights[id]) ? weights[id] : 100,
        })),
      });
      const socket = getSharedBffWebSocket(apiBaseUrl);
      socket.setAuthToken(user.token);
      const sent = socket.send(payload);
      setStatusNote(sent ? t('roundStartSetup.gamesUpdateSent') : t('roundStartSetup.gamesUpdateQueued'));
      logToBff('roundstart.round.update', {
        roundId,
        sent,
        gameCount: orderedGameIds.length,
      });
    },
    [apiBaseUrl, logToBff, roundGames, roundId, t, user?.token, weightsByGameId]
  );

  const clearGroupUpdateTimeout = useCallback(() => {
    if (!groupUpdateTimeoutRef.current) return;
    clearTimeout(groupUpdateTimeoutRef.current);
    groupUpdateTimeoutRef.current = null;
  }, []);

  const applyGroupOrder = useCallback(
    (playerOrder: string[]) => {
      setGroupLayout((previousLayout) => {
        if (!previousLayout) return previousLayout;
        if (!roundId) return previousLayout;
        return buildRoundGroupsLayout(roundId, previousLayout.baselinePlayerOrder, playerOrder);
      });
    },
    [roundId]
  );

  const resolveDropTargetPlayerId = useCallback((pageX: number, pageY: number, sourcePlayerId: string): string | null => {
    const entries = Object.entries(playerCardBoundsRef.current);
    for (const [playerId, bounds] of entries) {
      if (playerId === sourcePlayerId) continue;
      if (pageX >= bounds.left && pageX <= bounds.right && pageY >= bounds.top && pageY <= bounds.bottom) {
        return playerId;
      }
    }
    return null;
  }, []);

  const refreshPlayerCardBounds = useCallback(() => {
    Object.entries(playerCardRefs.current).forEach(([playerId, cardRef]) => {
      cardRef?.measureInWindow?.((x, y, width, height) => {
        if (!width || !height) return;
        playerCardBoundsRef.current[playerId] = {
          left: x,
          top: y,
          right: x + width,
          bottom: y + height,
        };
      });
    });
  }, []);

  const publishGroupOrderUpdate = useCallback(
    (nextOrder: string[], previousOrder: string[]) => {
      if (!roundId || !user?.token) return;
      const requestId = generateRequestId();
      pendingGroupUpdateRef.current = {
        requestId,
        previousOrder,
      };
      setGroupSavePending(true);
      setGroupSaveError(null);
      setStatusNote(t('roundStartSetup.groupsUpdateSending'));

      const socket = getSharedBffWebSocket(apiBaseUrl);
      socket.setAuthToken(user.token);
      const sent = socket.send({
        type: 'round.groups.update',
        requestId,
        roundId,
        playerOrder: nextOrder,
      });
      if (!sent) {
        pendingGroupUpdateRef.current = null;
        clearGroupUpdateTimeout();
        applyGroupOrder(previousOrder);
        setGroupSavePending(false);
        setGroupSaveError(t('roundStartSetup.groupsUpdateSendFailed'));
        setStatusNote(t('roundStartSetup.groupsUpdateQueued'));
        return;
      }

      clearGroupUpdateTimeout();
      groupUpdateTimeoutRef.current = setTimeout(() => {
        const pending = pendingGroupUpdateRef.current;
        if (!pending || pending.requestId !== requestId) return;
        pendingGroupUpdateRef.current = null;
        applyGroupOrder(pending.previousOrder);
        setGroupSavePending(false);
        setGroupSaveError(t('roundStartSetup.groupsUpdateTimeout'));
        setStatusNote(t('roundStartSetup.groupsUpdateTimeout'));
      }, GROUP_UPDATE_TIMEOUT_MS);
    },
    [apiBaseUrl, applyGroupOrder, clearGroupUpdateTimeout, roundId, t, user?.token]
  );

  const handleGroupPlayerPress = useCallback(
    (playerId: string) => {
      if (!roundId || !groupCanEdit || !groupLayout || groupSavePending) return;

      if (!selectedSwapPlayerId) {
        setSelectedSwapPlayerId(playerId);
        return;
      }

      if (selectedSwapPlayerId === playerId) {
        setSelectedSwapPlayerId(null);
        return;
      }

      const previousOrder = [...groupLayout.effectivePlayerOrder];
      const nextOrder = applyPlayerSwap(previousOrder, selectedSwapPlayerId, playerId);
      setSelectedSwapPlayerId(null);
      if (nextOrder.join('|') === previousOrder.join('|')) {
        return;
      }

      setGroupLayout(buildRoundGroupsLayout(roundId, groupLayout.baselinePlayerOrder, nextOrder));
      publishGroupOrderUpdate(nextOrder, previousOrder);
    },
    [groupCanEdit, groupLayout, groupSavePending, publishGroupOrderUpdate, roundId, selectedSwapPlayerId]
  );

  const handleDragStart = useCallback((playerId: string) => {
    if (!groupCanEdit || groupSavePending || !groupLayout) {
      return;
    }
    refreshPlayerCardBounds();
    setDraggingPlayerId(playerId);
    setDragHoverPlayerId(null);
  }, [groupCanEdit, groupLayout, groupSavePending, refreshPlayerCardBounds]);

  const handleDragMove = useCallback((playerId: string, pageX: number, pageY: number) => {
    if (draggingPlayerId !== playerId) return;
    const target = resolveDropTargetPlayerId(pageX, pageY, playerId);
    setDragHoverPlayerId(target);
  }, [draggingPlayerId, resolveDropTargetPlayerId]);

  const handleDragRelease = useCallback((playerId: string, pageX: number, pageY: number) => {
    if (!roundId || !groupCanEdit || !groupLayout || groupSavePending || draggingPlayerId !== playerId) {
      setDraggingPlayerId(null);
      setDragHoverPlayerId(null);
      return;
    }

    const targetPlayerId = resolveDropTargetPlayerId(pageX, pageY, playerId);
    setDraggingPlayerId(null);
    setDragHoverPlayerId(null);
    if (!targetPlayerId || targetPlayerId === playerId) {
      return;
    }
    setSelectedSwapPlayerId(null);

    const previousOrder = [...groupLayout.effectivePlayerOrder];
    const nextOrder = applyPlayerSwap(previousOrder, playerId, targetPlayerId);
    if (nextOrder.join('|') === previousOrder.join('|')) {
      return;
    }
    setGroupLayout(buildRoundGroupsLayout(roundId, groupLayout.baselinePlayerOrder, nextOrder));
    publishGroupOrderUpdate(nextOrder, previousOrder);
  }, [draggingPlayerId, groupCanEdit, groupLayout, groupSavePending, publishGroupOrderUpdate, resolveDropTargetPlayerId, roundId]);

  const handleDragCancel = useCallback(() => {
    setDraggingPlayerId(null);
    setDragHoverPlayerId(null);
  }, []);

  const handleTogglePlayerSelection = useCallback((playerId: string) => {
    setSelectedPlayersById((prev) => ({
      ...prev,
      [playerId]: !(prev[playerId] ?? true),
    }));
  }, []);

  const handlePlayingHandicapChange = useCallback((playerId: string, value: string) => {
    const normalized = value.replace(/[^0-9+\-.]/g, '');
    setPlayingHandicapByPlayerId((prev) => ({
      ...prev,
      [playerId]: normalized,
    }));
  }, []);

  const handleToggleGame = useCallback(
    (gameId: string) => {
      const next = new Set(selectedGameIds);
      if (next.has(gameId)) {
        next.delete(gameId);
      } else {
        next.add(gameId);
      }
      setSelectedGameIds(next);
      sendRoundUpdate(next);
    },
    [selectedGameIds, sendRoundUpdate]
  );

  const closeInfo = useCallback(() => {
    setInfoOpen(false);
    setInfoGame(null);
  }, []);

  const openInfo = useCallback((game: GameDefinition) => {
    setInfoGame(game);
    setInfoOpen(true);
  }, []);

  const resolveGameImageSource = useCallback((game: GameDefinition): ImageSourcePropType => {
    if (game.image && game.image.trim().length > 0) {
      return getAssetImageSource(game.image, 'game-select', MEDIA_PAGE_PATH);
    }
    return FALLBACK_GAME_IMAGE;
  }, []);

  const waitForRoundStatus = useCallback(
    async (expectedStatus: string, timeoutMs = 10000, intervalMs = 350): Promise<boolean> => {
      if (!roundId || !user?.token) return false;
      const expected = expectedStatus.trim().toLowerCase();
      const startedAt = Date.now();
      while (Date.now() - startedAt < timeoutMs) {
        try {
          const response = await fetch(`${apiBaseUrl}/api/rounds/${roundId}`, {
            headers: { Authorization: `Bearer ${user.token}` },
          });
          if (response.ok) {
            const data = (await response.json()) as RoundResponse;
            const statusRaw = data.status ?? data.Status ?? '';
            if (statusRaw.trim().toLowerCase() === expected) {
              return true;
            }
          }
        } catch {
          // Retry until timeout.
        }
        await new Promise((resolve) => setTimeout(resolve, intervalMs));
      }
      return false;
    },
    [apiBaseUrl, roundId, user?.token]
  );

  const handleOpenRound = useCallback(async () => {
    if (!roundId || !user?.token || openingRound || groupSavePending || !!groupSaveError) return;
    setOpeningRound(true);
    setError(null);
    setStatusNote(t('roundStartSetup.openRoundSending'));
    const normalizedCurrent: Record<string, string> = {};
    players.forEach((player) => {
      const raw = (playingHandicapByPlayerId[player.id] ?? '').trim();
      normalizedCurrent[player.id] = raw;
    });
    const hasShcpChanges = Object.keys(normalizedCurrent).some((playerId) => (savedPlayingHandicapByPlayerId[playerId] ?? '') !== normalizedCurrent[playerId]);
    if (hasShcpChanges) {
      try {
        const response = await fetch(`${apiBaseUrl}/api/rounds/${roundId}/players/shcp`, {
          method: 'PUT',
          headers: {
            Authorization: `Bearer ${user.token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            players: Object.entries(normalizedCurrent).map(([id, scp]) => ({ id, scp })),
          }),
        });
        if (!response.ok) {
          throw new Error(t('roundStartSetup.loadError'));
        }
        setSavedPlayingHandicapByPlayerId((prev) => ({ ...prev, ...normalizedCurrent }));
        setStatusNote(t('roundStartSetup.gamesUpdateSent'));
      } catch (saveError) {
        setOpeningRound(false);
        setError(saveError instanceof Error ? saveError.message : t('roundStartSetup.loadError'));
        return;
      }
    }
    setCurrentRoundId(roundId);
    const socket = getSharedBffWebSocket(apiBaseUrl);
    socket.setAuthToken(user.token);
    const sent = socket.send({ type: 'round.status.update', roundId, status: 'active' });
    if (!sent) {
      setOpeningRound(false);
      setStatusNote(t('roundStartSetup.openRoundQueued'));
      setError(t('roundStartSetup.openRoundSendFailed'));
      return;
    }
    const confirmed = await waitForRoundStatus('active');
    if (!confirmed) {
      setOpeningRound(false);
      setStatusNote(t('roundStartSetup.openRoundQueued'));
      setError(t('roundStartSetup.openRoundTimeout'));
      return;
    }
    setStatusNote(t('roundStartSetup.openRoundConfirmed'));
    const params: string[] = [];
    if (debug) params.push('debug=true');
    if (tourId) params.push(`tourId=${encodeURIComponent(tourId)}`);
    const query = params.length ? `?${params.join('&')}` : '';
    router.push(`/round/${roundId}/hole/1/compact${query}`);
  }, [
    apiBaseUrl,
    debug,
    groupSaveError,
    groupSavePending,
    openingRound,
    players,
    playingHandicapByPlayerId,
    roundId,
    router,
    savedPlayingHandicapByPlayerId,
    t,
    tourId,
    user?.token,
    waitForRoundStatus,
  ]);

  const handleSelectGames = useCallback(() => {
    if (!roundId) return;
    const params: string[] = ['source=roundstart'];
    if (debug) params.push('debug=true');
    if (tourId) params.push(`tourId=${encodeURIComponent(tourId)}`);
    const query = params.length ? `?${params.join('&')}` : '';
    router.push(`/round/${roundId}/select-games${query}`);
  }, [debug, roundId, router, tourId]);

  const handleOpenShareModal = useCallback(async () => {
    if (!tourId || !user?.token) return;
    setShareModalLoading(true);
    setShareModalError(null);
    setShareModalLink(null);
    setShareModalOpen(true);
    try {
      const inviteLink = await resolveTourInviteLink({
        apiBaseUrl,
        tourId,
        authToken: user.token,
      });
      setShareModalLink(inviteLink);
    } catch (shareError) {
      setShareModalError(t('tournaments.shareLoadError'));
      setStatusNote(t('tournaments.shareLoadError'));
    } finally {
      setShareModalLoading(false);
    }
  }, [apiBaseUrl, t, tourId, user?.token]);

  const shareJoinCode = useMemo(() => {
    if (!shareModalLink) {
      return '';
    }
    try {
      const token = decodeURIComponent(shareModalLink.split('/').pop() ?? '')
        .replace(/[^a-zA-Z0-9]/g, '')
        .toUpperCase();
      if (!token) {
        return '';
      }
      const compact = token.slice(0, 6);
      if (compact.length <= 3) {
        return compact;
      }
      return compact;
    } catch {
      return '';
    }
  }, [shareModalLink]);

  const handleShareInviteLink = useCallback(async () => {
    if (!shareModalLink) {
      return;
    }

    const title = t('tournaments.shareTitle', { name: roundName ?? t('tournaments.defaultName') });
    try {
      if (Platform.OS === 'web' && typeof navigator !== 'undefined') {
        const navWithShare = navigator as {
          share?: (data: { title?: string; text?: string; url?: string }) => Promise<void>;
          clipboard?: { writeText?: (value: string) => Promise<void> };
        };

        if (typeof navWithShare.share === 'function') {
          await navWithShare.share({
            title,
            url: shareModalLink,
          });
          return;
        }

        if (navWithShare.clipboard?.writeText) {
          await navWithShare.clipboard.writeText(shareModalLink);
          setStatusNote(t('tournaments.shareCopied'));
          return;
        }

        throw new Error('share unsupported');
      }

      await Share.share({
        title,
        message: shareModalLink,
      });
    } catch (shareError) {
      const message = shareError instanceof Error ? shareError.message : String(shareError);
      const canceled = message.toLowerCase().includes('cancel');
      if (!canceled) {
        setShareModalError(t('tournaments.shareSendError'));
      }
    }
  }, [roundName, shareModalLink, t]);

  useEffect(() => {
    if (!roundId) return;
    setCurrentRoundId(roundId);
  }, [roundId]);

  useEffect(() => {
    if (!isNavigationReady || !isFocused || !roundId) return;
    const socket = getSharedBffWebSocket(apiBaseUrl);
    socket.setSubscriptionContext({ roundId });
  }, [apiBaseUrl, isFocused, isNavigationReady, roundId]);

  useEffect(() => {
    if (!isNavigationReady || !user?.token || !roundId) {
      setLoading(false);
      return;
    }
    void loadSnapshot();
    const socket = getSharedBffWebSocket(apiBaseUrl);
    socket.setAuthToken(user.token);
    const unsubscribeMessages = socket.onMessage((message) => {
      const type = (message as { type?: string }).type;
      if (type === 'round.updated') {
        const payload = message as RoundUpdatedEvent;
        if (payload.roundId !== roundId) return;
        if (payload.gameDefinitionIds) {
          setSelectedGameIds(new Set(payload.gameDefinitionIds));
        }
        if (payload.gameWeights) {
          setWeightsByGameId((prev) => {
            const next = { ...prev };
            payload.gameWeights?.forEach((entry) => {
              if (entry.gameDefinitionId && typeof entry.weightPercent === 'number' && !Number.isNaN(entry.weightPercent)) {
                next[entry.gameDefinitionId] = entry.weightPercent;
              }
            });
            return next;
          });
        }
      } else if (type === 'round.groups.updated') {
        const payload = message as RoundGroupsUpdatedEvent;
        if (payload.roundId !== roundId) return;
        const incomingOrder = (payload.playerOrder ?? []).filter((playerId): playerId is string => typeof playerId === 'string');
        if (incomingOrder.length > 0) {
          applyGroupOrder(incomingOrder);
        }
        const pending = pendingGroupUpdateRef.current;
        if (pending && payload.requestId && pending.requestId === payload.requestId) {
          pendingGroupUpdateRef.current = null;
          clearGroupUpdateTimeout();
          setGroupSavePending(false);
          setGroupSaveError(null);
          setStatusNote(t('roundStartSetup.groupsUpdateSaved'));
        }
      } else if (type === 'round.groups.update.rejected') {
        const payload = message as RoundGroupsUpdateRejectedEvent;
        if (payload.roundId !== roundId) return;
        const pending = pendingGroupUpdateRef.current;
        if (pending && payload.requestId && pending.requestId !== payload.requestId) {
          return;
        }
        if (pending) {
          applyGroupOrder(pending.previousOrder);
        }
        pendingGroupUpdateRef.current = null;
        clearGroupUpdateTimeout();
        setGroupSavePending(false);
        setSelectedSwapPlayerId(null);
        const messageText = payload.message?.trim() || t('roundStartSetup.groupsUpdateRejected');
        setGroupSaveError(messageText);
        setStatusNote(messageText);
      }
    });
    const unsubscribeErrors = socket.onError((message) => setError(message));
    return () => {
      unsubscribeMessages();
      unsubscribeErrors();
      clearGroupUpdateTimeout();
      pendingGroupUpdateRef.current = null;
    };
  }, [apiBaseUrl, applyGroupOrder, clearGroupUpdateTimeout, isNavigationReady, loadSnapshot, roundId, t, user?.token]);

  useEffect(() => {
    if (!groupLayout) return;
    const timer = setTimeout(() => refreshPlayerCardBounds(), 0);
    return () => clearTimeout(timer);
  }, [groupLayout, refreshPlayerCardBounds]);

  if (!isNavigationReady) return null;
  if (!user) return <Redirect href="/" />;
  if (!roundId) return <Redirect href="/my-tours" />;

  return (
    <ScrollView style={styles.scroll} contentContainerStyle={styles.container}>
      <Text style={styles.subtitle}>
        {roundName ? t('roundStartSetup.subtitleNamed', { name: roundName }) : t('roundStartSetup.subtitleFallback')}
      </Text>
      {loading ? (
        <View style={styles.loadingRow}>
          <ActivityIndicator size="small" color="#A1D493" />
          <Text style={styles.meta}>{t('roundStartSetup.loading')}</Text>
        </View>
      ) : null}
      {statusNote ? <Text style={styles.meta}>{statusNote}</Text> : null}
      {error ? <Text style={styles.error}>{error}</Text> : null}

      <View style={styles.section}>
        <View style={styles.playersHeaderRow}>
          <Text style={styles.sectionTitle}>{t('roundStartSetup.playersTitle')}</Text>
          <Text style={styles.playersHandicapHeader}>{t('roundStartSetup.playingHandicapLabel')}</Text>
        </View>
        {players.length === 0 ? <Text style={styles.meta}>{t('roundStartSetup.playersEmpty')}</Text> : null}
        {players.map((player) => {
          const enabled = selectedPlayersById[player.id] ?? true;
          return (
            <View key={player.id} style={[styles.row, !enabled && styles.playerRowDisabled]}>
              <Pressable
                style={styles.checkboxWrap}
                onPress={() => handleTogglePlayerSelection(player.id)}
                accessibilityRole="checkbox"
                accessibilityState={{ checked: enabled }}
              >
                <View style={[styles.checkbox, enabled && styles.checkboxChecked]}>
                  {enabled ? <Text style={styles.checkboxTick}>✓</Text> : null}
                </View>
              </Pressable>
              <View style={styles.playerInfoBlock}>
                <Text style={[styles.rowTitle, !enabled && styles.rowTitleDisabled]}>{player.name || player.id}</Text>
              </View>
              <View style={styles.handicapWrap}>
                <TextInput
                  value={playingHandicapByPlayerId[player.id] ?? ''}
                  onChangeText={(value) => handlePlayingHandicapChange(player.id, value)}
                  placeholder={t('roundStartSetup.playingHandicapPlaceholder')}
                  placeholderTextColor="#A1D493"
                  keyboardType="numbers-and-punctuation"
                  editable={enabled}
                  style={[styles.handicapInput, !enabled && styles.handicapInputDisabled]}
                />
              </View>
            </View>
          );
        })}
      </View>

      <View style={styles.section}>
        <Text style={styles.sectionTitle}>{t('roundStartSetup.groupsTitle')}</Text>
        <Text style={styles.meta}>
          {groupCanEdit ? t('roundStartSetup.groupsHint') : t('roundStartSetup.groupsLocked')}
        </Text>
        {groupSavePending ? <Text style={styles.meta}>{t('roundStartSetup.groupsUpdateSending')}</Text> : null}
        {groupSaveError ? <Text style={styles.error}>{groupSaveError}</Text> : null}
        {!groupLayout ? <Text style={styles.meta}>{t('roundStartSetup.groupsEmpty')}</Text> : null}
        {groupLayout?.groups.map((group, groupIndex) => (
          <View key={group.groupId} style={styles.groupBoard}>
            <Text style={styles.groupTitle}>{t('roundStartSetup.groupLabel', { index: groupIndex + 1 })}</Text>
            {group.teams.map((team, teamIndex) => (
              <View key={team.teamId} style={styles.teamBoard}>
                <Text style={styles.teamTitle}>{t('roundStartSetup.teamLabel', { index: teamIndex + 1 })}</Text>
                <View style={styles.teamPlayers}>
                  {team.playerIds.map((playerId) => (
                    <Pressable
                      key={playerId}
                      ref={(node) => {
                        playerCardRefs.current[playerId] = node as MeasurableCard | null;
                      }}
                      onPress={() => handleGroupPlayerPress(playerId)}
                      disabled={!groupCanEdit || groupSavePending}
                      onLayout={() => refreshPlayerCardBounds()}
                      onStartShouldSetResponder={() => groupCanEdit && !groupSavePending}
                      onMoveShouldSetResponder={() => groupCanEdit && !groupSavePending}
                      onResponderGrant={() => handleDragStart(playerId)}
                      onResponderMove={(event) =>
                        handleDragMove(playerId, event.nativeEvent.pageX, event.nativeEvent.pageY)
                      }
                      onResponderRelease={(event) =>
                        handleDragRelease(playerId, event.nativeEvent.pageX, event.nativeEvent.pageY)
                      }
                      onResponderTerminate={handleDragCancel}
                      style={[
                        styles.groupPlayerCard,
                        selectedSwapPlayerId === playerId && styles.groupPlayerCardSelected,
                        draggingPlayerId === playerId && styles.groupPlayerCardDragging,
                        dragHoverPlayerId === playerId && styles.groupPlayerCardDropTarget,
                        (!groupCanEdit || groupSavePending) && styles.groupPlayerCardDisabled,
                      ]}
                    >
                      <Text style={styles.groupPlayerName}>{playerNameById[playerId] ?? playerId}</Text>
                    </Pressable>
                  ))}
                </View>
              </View>
            ))}
          </View>
        ))}
      </View>

      <View style={styles.section}>
        <Text style={styles.sectionTitle}>{t('roundStartSetup.gamesTitle')}</Text>
        {roundGames.length === 0 ? <Text style={styles.meta}>{t('roundStartSetup.gamesEmpty')}</Text> : null}
        {roundGames.map((game) => {
          const enabled = selectedGameIds.has(game.id);
          const gameImageSource = resolveGameImageSource(game);
          return (
            <View key={game.id} style={styles.row}>
              <Pressable style={styles.checkboxWrap} onPress={() => handleToggleGame(game.id)} accessibilityRole="checkbox" accessibilityState={{ checked: enabled }}>
                <View style={[styles.checkbox, enabled && styles.checkboxChecked]}>
                  {enabled ? <Text style={styles.checkboxTick}>✓</Text> : null}
                </View>
              </Pressable>
              <Pressable style={styles.gameIconWrap} onPress={() => openInfo(game)} accessibilityLabel={t('selectGames.gameInfo')}>
                <Image source={gameImageSource} style={styles.gameIcon} resizeMode="cover" />
              </Pressable>
              <View style={styles.gameInfoBlock}>
                <Text style={styles.rowTitle}>{game.name || game.id}</Text>
                {game.info ? <Text style={styles.gameInfoText} numberOfLines={2}>{game.info}</Text> : null}
              </View>
              <View style={styles.gameActions}>
                <Pressable style={styles.gameInfoButton} onPress={() => openInfo(game)} accessibilityLabel={t('selectGames.gameInfo')}>
                  <Text style={styles.gameInfoButtonText}>{t('selectGames.infoIcon')}</Text>
                </Pressable>
              </View>
            </View>
          );
        })}
      <GoffyButton
        label={t('roundStartSetup.addMoreGamesCta')}
        onPress={handleSelectGames}
          tone="dark"
          oldButtonStyle={styles.secondaryButton}
          oldTextStyle={styles.secondaryButtonText}
          oldDisabledStyle={styles.disabledButton}
        style={styles.primaryButtonWide}
      />
      <GoffyButton
        label={t('tournaments.shareAction')}
        onPress={() => void handleOpenShareModal()}
        tone="dark"
        disabled={!tourId || shareModalLoading}
        size="compact"
        style={styles.shareRoundButton}
      />
      </View>

      <GoffyButton
        label={
          openingRound
            ? t('roundStartSetup.openRoundSending')
            : updateMode
              ? t('roundStartSetup.updateCta')
              : t('roundStartSetup.openRoundCta')
        }
        onPress={handleOpenRound}
        tone="accent"
        disabled={openingRound || groupSavePending || !!groupSaveError}
        oldButtonStyle={styles.primaryButton}
        oldTextStyle={styles.primaryButtonText}
        oldDisabledStyle={styles.disabledButton}
        style={styles.primaryButtonWide}
      />

      <RoundGameInfoModal
        visible={infoOpen}
        game={infoGame}
        apiBaseUrl={apiBaseUrl}
        onClose={closeInfo}
      />
      <Modal
        transparent
        visible={shareModalOpen}
        animationType="fade"
        onRequestClose={() => setShareModalOpen(false)}
      >
        <Pressable style={styles.shareOverlay} onPress={() => setShareModalOpen(false)}>
          <Pressable style={styles.shareCard} onPress={() => null}>
            <Text style={styles.shareTitle}>{t('players.inviteOthers')}</Text>
            <View style={styles.shareCodeCard}>
              <Text style={styles.shareCodeLabel}>{t('players.joinWithCode')}</Text>
              <Text style={styles.shareCodeValue}>{shareJoinCode || '--- ---'}</Text>
              {shareModalLoading ? (
                <ActivityIndicator size="small" color={appColors.accent.primary} />
              ) : null}
              {shareModalLink ? (
                <View style={styles.shareQrWrapper}>
                  <QRCode value={shareModalLink} size={220} color="#000000" backgroundColor="#ffffff" />
                </View>
              ) : null}
            </View>
            {shareModalError ? <Text style={styles.error}>{shareModalError}</Text> : null}
            <View style={styles.shareActions}>
              <GoffyButton
                label={t('tournaments.shareAction')}
                onPress={() => void handleShareInviteLink()}
                disabled={!shareModalLink || shareModalLoading}
                tone="accent"
                size="compact"
                style={styles.shareActionButton}
              />
              <GoffyButton
                label={t('tournaments.shareClose')}
                onPress={() => setShareModalOpen(false)}
                tone="dark"
                size="compact"
                style={styles.shareActionButton}
              />
            </View>
          </Pressable>
        </Pressable>
      </Modal>
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  scroll: {
    backgroundColor: '#011806',
  },
  container: {
    padding: 20,
    gap: 12,
  },
  title: {
    color: '#C8C8B0',
    fontSize: 24,
    fontWeight: '800',
  },
  subtitle: {
    color: '#A1D493',
    fontSize: 14,
  },
  meta: {
    color: '#A1D493',
    fontSize: 12,
  },
  error: {
    color: '#ff9fa6',
    fontSize: 13,
    fontWeight: '600',
  },
  loadingRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  section: {
    backgroundColor: '#07200D',
    borderColor: '#1A3A20',
    borderWidth: 1,
    borderRadius: 12,
    padding: 12,
    gap: 8,
  },
  sectionTitle: {
    color: appColors.text.primary,
    fontSize: 15,
    fontWeight: '700',
  },
  groupBoard: {
    borderWidth: 1,
    borderColor: '#A1D493',
    borderRadius: 10,
    padding: 10,
    gap: 8,
    backgroundColor: appColors.surface.alt,
  },
  groupTitle: {
    color: appColors.text.primary,
    fontSize: 13,
    fontWeight: '700',
  },
  teamBoard: {
    borderWidth: 1,
    borderColor: '#A1D493',
    borderRadius: 8,
    padding: 8,
    gap: 6,
    backgroundColor: '#C8C8B0',
  },
  teamTitle: {
    color: appColors.text.primary,
    fontSize: 12,
    fontWeight: '700',
  },
  teamPlayers: {
    gap: 6,
  },
  groupPlayerCard: {
    borderWidth: 1,
    borderColor: '#A1D493',
    borderRadius: 8,
    paddingVertical: 8,
    paddingHorizontal: 10,
    backgroundColor: '#1A3A20',
  },
  groupPlayerCardSelected: {
    borderColor: '#A1D493',
    backgroundColor: '#A1D493',
  },
  groupPlayerCardDragging: {
    opacity: 0.8,
    borderColor: '#f59e0b',
  },
  groupPlayerCardDropTarget: {
    borderColor: '#A1D493',
    backgroundColor: '#C8C8B0',
  },
  groupPlayerCardDisabled: {
    opacity: 0.65,
  },
  groupPlayerName: {
    color: '#C8C8B0',
    fontSize: 13,
    fontWeight: '600',
  },
  playersHeaderRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  playersHandicapHeader: {
    color: '#A1D493',
    fontSize: 12,
    fontWeight: '700',
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 10,
    backgroundColor: '#07200D',
    borderColor: '#1A3A20',
    borderWidth: 1,
    borderRadius: 10,
    paddingHorizontal: 10,
    paddingVertical: 8,
  },
  rowTitle: {
    color: '#C8C8B0',
    fontSize: 14,
    fontWeight: '600',
    flex: 1,
  },
  rowTitleDisabled: {
    color: '#8FA08B',
  },
  playerRowDisabled: {
    backgroundColor: appColors.surface.alt,
    borderColor: '#1A3A20',
    opacity: 0.75,
  },
  playerInfoBlock: {
    flex: 1,
  },
  checkboxWrap: {
    alignSelf: 'center',
  },
  checkbox: {
    width: 22,
    height: 22,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#A1D493',
    backgroundColor: '#07200D',
    alignItems: 'center',
    justifyContent: 'center',
  },
  checkboxChecked: {
    backgroundColor: '#A1D493',
    borderColor: '#A1D493',
  },
  checkboxTick: {
    color: appColors.text.primary,
    fontSize: 14,
    fontWeight: '800',
    lineHeight: 16,
  },
  gameIconWrap: {
    borderRadius: 8,
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: '#1A3A20',
  },
  gameIcon: {
    width: 42,
    height: 42,
  },
  gameInfoBlock: {
    flex: 1,
    gap: 2,
  },
  gameInfoText: {
    color: '#A1D493',
    fontSize: 12,
  },
  gameActions: {
    alignItems: 'flex-end',
    justifyContent: 'center',
    gap: 6,
  },
  gameInfoButton: {
    width: 24,
    height: 24,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#A1D493',
    backgroundColor: '#F2FAF1',
    alignItems: 'center',
    justifyContent: 'center',
  },
  gameInfoButtonText: {
    color: '#0B2E1A',
    fontSize: 15,
    fontWeight: '800',
    lineHeight: 14,
  },
  handicapWrap: {
    width: 92,
    gap: 0,
  },
  handicapInput: {
    backgroundColor: '#07200D',
    color: appColors.text.primary,
    borderWidth: 1,
    borderColor: '#1A3A20',
    borderRadius: 8,
    paddingHorizontal: 8,
    paddingVertical: 6,
    fontSize: 13,
    fontWeight: '700',
    textAlign: 'center',
  },
  handicapInputDisabled: {
    backgroundColor: '#07200D',
    borderColor: '#1A3A20',
    color: '#8FA08B',
    opacity: 0.75,
  },
  primaryButton: {
    borderRadius: 999,
    borderWidth: 1,
    borderColor: 'rgba(42,119,66,0.4)',
    backgroundColor: 'rgba(45,98,53,0.18)',
    paddingHorizontal: 12,
    paddingVertical: 6,
  },
  primaryButtonText: {
    color: appColors.text.primary,
    fontSize: 14,
    fontWeight: '700',
  },
  secondaryButton: {
    borderRadius: 999,
    borderWidth: 1,
    borderColor: '#A1D493',
    backgroundColor: '#C8C8B0',
    paddingHorizontal: 10,
    paddingVertical: 4,
  },
  secondaryButtonText: {
    color: appColors.text.primary,
    fontSize: 13,
    fontWeight: '700',
  },
  disabledButton: {
    opacity: 0.6,
  },
  primaryButtonWide: {
    width: '100%',
    marginTop: 4,
  },
  shareRoundButton: {
    marginTop: 4,
    alignSelf: 'center',
  },
  shareOverlay: {
    flex: 1,
    backgroundColor: 'rgba(17,44,27,0.22)',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 20,
  },
  shareCard: {
    width: '100%',
    maxWidth: 420,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: appColors.border.strong,
    backgroundColor: '#07200D',
    padding: 18,
    gap: 12,
  },
  shareTitle: {
    color: appColors.text.primary,
    fontSize: 22,
    fontWeight: '800',
    textAlign: 'center',
  },
  shareCodeCard: {
    borderRadius: 14,
    borderWidth: 1,
    borderColor: '#1A3A20',
    backgroundColor: '#04210D',
    padding: 16,
    gap: 10,
    alignItems: 'center',
  },
  shareCodeLabel: {
    color: '#8FA08B',
    fontSize: 12,
    letterSpacing: 3,
    textTransform: 'uppercase',
    fontWeight: '700',
  },
  shareCodeValue: {
    color: '#AEE79A',
    fontSize: 48,
    letterSpacing: 6,
    fontWeight: '700',
    textAlign: 'center',
  },
  shareQrWrapper: {
    alignSelf: 'center',
    borderRadius: 12,
    backgroundColor: '#f6f8f6',
    padding: 10,
  },
  shareActions: {
    flexDirection: 'column',
    gap: 10,
    justifyContent: 'flex-start',
  },
  shareActionButton: {
    width: '100%',
  },
});
